// Small DOM building blocks shared by the administration tabs: section headers, loading / error /
// unavailable states, a copy button, a textarea with a character counter, a color picker, a
// form-level error box, an alert line, a link to a new tab (the login windows) and focus
// restoration across re-renders. Text always goes through text
// nodes (web/dom.js); dynamic colors go through CSS custom properties.
import { h, button, emptyState, spinner, classNames, icon, uid, setAttr, setText } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { deptColor } from '../../format.js';
import { COLORS } from './model.js';
import './strings.js';

/**
 * Header of a tab: serif title, optional count, intro text and actions on the right.
 * @param {{title: string, count?: string, intro?: string, actions?: Node[]}} opts
 */
export function sectionHead({ title, count, intro, actions = [] }) {
  const countEl = h('span', { class: 'adm-count mono-label' }, count || '');
  const el = h('div', { class: 'adm-head' },
    h('div', { class: 'adm-head-text' },
      h('h3', { class: 'adm-title' }, title, countEl),
      intro ? h('p', { class: 'adm-intro' }, intro) : null),
    actions.length ? h('div', { class: 'adm-head-actions' }, actions) : null);
  el.setCount = (text) => { countEl.textContent = text || ''; };
  return el;
}

/** A centred loading state. */
export function loadingState(label = t('adm.loading')) {
  return h('div', { class: 'adm-loading', role: 'status' }, spinner(label), h('span', { class: 'muted' }, label));
}

/**
 * State shown when a request failed: 404 → "not available yet", else the message and a retry.
 * @param {Error & {status?: number}} err
 * @param {{onRetry?: Function, unavailableText?: string, icon?: string}} [opts]
 */
export function errorState(err, { onRetry, unavailableText, icon: iconName = 'alert' } = {}) {
  if (err?.status === 404) {
    return emptyState({ icon: 'info', title: t('common.soon'), text: unavailableText || t('adm.unavailable') });
  }
  return emptyState({
    icon: iconName, title: t('adm.loadError'), text: err?.message || t('common.error'),
    action: onRetry ? button(t('adm.retry'), { variant: 'secondary', icon: 'refresh', onClick: onRetry }) : null,
  });
}

/** Writes text to the clipboard; resolves true on success. */
export async function copyText(text) {
  try {
    const clip = globalThis.navigator?.clipboard;
    if (clip?.writeText) { await clip.writeText(String(text)); return true; }
  } catch { /* permission refused or insecure context */ }
  return false;
}

/**
 * A "COPIER" button. On failure it selects `selectEl` (when given) and shows a hint toast.
 * @param {() => string} getText
 * @param {{toast?: object, selectEl?: HTMLInputElement, size?: string, variant?: string, label?: string}} [opts]
 */
export function copyButton(getText, { toast, selectEl, size = 'sm', variant = 'secondary', label } = {}) {
  const btn = button(label || t('adm.copy'), {
    variant, size,
    onClick: async () => {
      const ok = await copyText(getText());
      if (ok) toast?.success?.(t('adm.copied'), { timeout: 2000 });
      else {
        selectEl?.focus?.();
        selectEl?.select?.();
        toast?.info?.(t('adm.copyManual'));
      }
    },
  });
  return btn;
}

/**
 * A textarea with a live "n / max" counter. The wrapper exposes `.input` (the textarea), so
 * field() labels it correctly.
 * @param {{value?: string, max: number, rows?: number, placeholder?: string, name?: string}} opts
 * @returns {HTMLElement & {input: HTMLTextAreaElement}}
 */
export function countedTextarea({ value = '', max, rows = 6, placeholder, name } = {}) {
  const area = h('textarea', {
    class: 'input textarea', rows, placeholder, name, maxLength: max, spellcheck: true,
  });
  area.value = value;
  const counter = h('span', { class: 'adm-counter mono-label', 'aria-hidden': 'true' });
  const update = () => {
    const n = area.value.length;
    counter.textContent = t('adm.counter', { n, max });
    counter.classList.toggle('is-near', n >= max * 0.9 && n < max);
    counter.classList.toggle('is-full', n >= max);
  };
  area.addEventListener('input', update);
  update();
  const wrap = h('div', { class: 'adm-counted' }, area, counter);
  wrap.input = area;
  return wrap;
}

/**
 * A color picker: a radio group of the palette swatches (arrow keys move the choice). A custom
 * #hex value (allowed by the SPEC) is kept as an extra swatch.
 * @param {{value?: string, label: string, onChange?: (color: string) => void}} opts
 * @returns {HTMLFieldSetElement & {value: string, setError(msg: string|null): void}}
 */
export function colorPicker({ value = '', label, onChange } = {}) {
  const options = [...COLORS];
  if (value && !COLORS.includes(value)) options.push(value);
  const group = h('div', { class: 'adm-swatches', role: 'radiogroup', 'aria-label': label });
  const err = h('p', { class: 'field-error', role: 'alert', hidden: true });
  const set = new Map();
  const el = h('fieldset', { class: 'adm-fieldset field' }, h('legend', { class: 'field-label' }, label), group, err);
  const select = (v, emit, focus) => {
    el.value = v;
    for (const [key, b] of set) {
      b.setAttribute('aria-checked', String(key === v));
      b.tabIndex = key === v ? 0 : -1;
    }
    if (focus) set.get(v)?.focus();
    if (emit) onChange?.(v);
  };
  for (const c of options) {
    const name = COLORS.includes(c) ? t(`adm.color.${c}`) : t('adm.color.custom', { hex: c });
    const b = h('button', {
      type: 'button', role: 'radio', class: 'adm-swatch', title: name, 'aria-label': name,
      style: { '--swatch': deptColor(c) },
      onClick: () => { select(c, true, false); err.hidden = true; },
    }, icon('check', { size: 14, class: 'adm-swatch-check' }));
    set.set(c, b);
    group.appendChild(b);
  }
  group.addEventListener('keydown', (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const idx = Math.max(0, options.indexOf(el.value));
    const next = options[(idx + (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1) + options.length) % options.length];
    select(next, true, true);
  });
  select(options.includes(value) ? value : '', false, false);
  if (!el.value) { const first = set.get(options[0]); if (first) first.tabIndex = 0; }
  el.setError = (msg) => { err.textContent = msg || ''; err.hidden = !msg; };
  return el;
}

/** A form-level error box (role alert), hidden until set. */
export function formError() {
  const el = h('div', { class: 'adm-form-error', role: 'alert', hidden: true },
    icon('alert', { size: 16 }), h('span', {}));
  el.set = (msg) => {
    el.lastChild.textContent = msg || '';
    el.hidden = !msg;
  };
  return el;
}

/** An alert line (.adm-alert) with its icon. */
export function alertBox(tone, ...content) {
  return h('p', { class: ['adm-alert', `is-${tone}`] }, icon(tone === 'info' ? 'info' : 'alert', { size: 16 }), h('span', {}, ...content));
}

/**
 * A link opening in a new tab (never the opener's context). `primary` styles it as a button. The
 * href goes through setAttr(), which refuses script URLs; an empty href leaves the link inert.
 */
export function externalLink(href, label, { primary = false } = {}) {
  const a = h('a', {
    class: primary ? 'btn btn-primary adm-acc-link' : 'adm-acc-extlink', target: '_blank', rel: 'noopener noreferrer',
  }, h('span', { class: primary ? 'btn-label' : null }, label), h('span', { class: 'sr-only' }, ` ${t('adm.acc.newTab')}`),
  icon('arrowRight', { size: 14 }));
  if (href) setAttr(a, 'href', href);
  return a;
}

/** Replaces the text of a button built with dom.button(). */
export function setLabel(btn, text) {
  const label = btn.querySelector('.btn-label');
  if (label) setText(label, text);
}

/** Toggles the busy look of a button built with dom.button(). */
export function setBusy(btn, busy) {
  if (!btn) return;
  btn.disabled = !!busy;
  btn.classList.toggle('is-busy', !!busy);
  if (busy) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
}

/**
 * Keeps track of focusable controls by a stable key across a re-render, so the focus stays on
 * "the same" control after the list is rebuilt (e.g. after moving a floor up).
 */
export function focusKeeper() {
  let map = new Map();
  return {
    /** Registers `el` under `key` for the current render; returns el. */
    reg(key, el) { map.set(key, el); el.dataset.fkey = key; return el; },
    /** Starts a new render; returns a restore() to call once the new DOM is in place. */
    begin() {
      const active = globalThis.document?.activeElement;
      const key = active?.dataset?.fkey || null;
      map = new Map();
      return () => { if (key && map.has(key)) map.get(key).focus?.({ preventScroll: true }); };
    },
  };
}

/** A mono label + value pair for cards ("VERSION 2.1.3"). */
export function metaItem(label, value, { mono = false } = {}) {
  return h('div', { class: 'adm-meta' },
    h('span', { class: 'adm-meta-label mono-label' }, label),
    h('span', { class: classNames(['adm-meta-value', { 'is-mono': mono }]) }, value ?? '—'));
}

/** An id for aria wiring. */
export const aid = (prefix) => uid(`adm-${prefix}`);

/** A line of shell command with a copy button (engine hints). */
export function commandLine(cmd, ctx) {
  const code = h('code', { class: 'adm-cmd-code' }, cmd);
  return h('div', { class: 'adm-cmd' }, code, copyButton(() => cmd, { toast: ctx?.toast, variant: 'ghost' }));
}
